const initializeTransactionsList = (
  componentInstance,
  listWrapperRef,
  bufferPxHeight,
  scrollHandlerThrottle
) => {
  console.debug("virtual scroll js is initializing for:", listWrapperRef, componentInstance);

  // Method names for component callbacks.
  const HandleNextPage = "HandleNextPage";
  const HandlePreviousPage = "HandlePreviousPage";
  const HandleRowVisibilityChange = "HandleRowVisibilityChange";
  const HandleWindowResize = "HandleWindowResize";

  // Helper: Check if an element is scrollable vertically.
  const isScrollable = (ele) =>
    window.getComputedStyle(ele).overflowY.indexOf("auto") !== -1;

  // Helper: Recursively find the nearest scrollable parent.
  const getScrollableParent = (ele) =>
    !ele || ele === document.body ? document.body : (isScrollable(ele) ? ele : getScrollableParent(ele.parentNode));

  // Identify the scroll container.
  const scrollBox = getScrollableParent(listWrapperRef);

  // Calculate buffered height based on the scroll container height plus extra buffer.
  let bufferedScrollBoxHeight = scrollBox.clientHeight + 2 * bufferPxHeight;

  // ---------------------------
  // Scroll Handling (rAF Throttled)
  // ---------------------------
  let isScrollScheduled = false;

  const onScroll = () => {
    if (!isScrollScheduled) {
      isScrollScheduled = true;
      window.requestAnimationFrame(async () => {
        await handleScroll();
        isScrollScheduled = false;
      });
    }
  };

  // ---------------------------
  // Resize Handling (Debounced)
  // ---------------------------
  let resizeTimer = null;

  const onResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(async () => {
      await handleWindowResize();
      resizeTimer = null;
    }, scrollHandlerThrottle);
  };

  // ---------------------------
  // DOM Measurements
  // ---------------------------
  const getAllMeasurements = () => {
    const topSpacer = listWrapperRef.querySelector(".top-spacer");
    const bottomSpacer = listWrapperRef.querySelector(".bottom-spacer");

    if (scrollBox && topSpacer && bottomSpacer) {
      const scrollBoxHeight = scrollBox.clientHeight;
      const scrollBoxScrollHeight = scrollBox.scrollHeight;

      const scrollBoxPosition = scrollBox.getBoundingClientRect();
      const topSpacerPosition = topSpacer.getBoundingClientRect();
      const bottomSpacerPosition = bottomSpacer.getBoundingClientRect();
      const listWrapperPosition = listWrapperRef.getBoundingClientRect();

      const scrollBoxOffsetTop = scrollBoxPosition.top - bufferPxHeight;
      const scrollBoxOffsetBottom = scrollBoxPosition.bottom + bufferPxHeight;

      const bottomSpacerOffset = bottomSpacerPosition.top - scrollBoxOffsetTop;
      const bottomSpacerHeight = bottomSpacer.offsetHeight;

      const topSpacerOffset = scrollBoxOffsetBottom - topSpacerPosition.bottom;
      const topSpacerHeight = topSpacer.offsetHeight;

      const visibleHeaderHeight = Math.max(topSpacerPosition.top - scrollBoxOffsetTop, 0);
      const visibleFooterHeight = Math.max(scrollBoxOffsetBottom - bottomSpacerPosition.bottom, 0);

      // Calculate the heights of rows completely outside the scroll box.
      const topHiddenRowsHeight = scrollBoxPosition.top - topSpacerPosition.bottom;
      const bottomHiddenRowsHeight = bottomSpacerPosition.top - scrollBoxPosition.top - scrollBoxHeight;

      return {
        ScrollBoxHeight: Math.trunc(scrollBoxHeight),
        ScrollBoxScrollHeight: Math.trunc(scrollBoxScrollHeight),
        ScrollBoxHeaderFooterHeight: Math.trunc(scrollBox.scrollTop + (listWrapperPosition.top - scrollBoxPosition.top)),
        ListWrapperTopOffset: Math.trunc(listWrapperPosition.top),
        BottomSpacerOffset: Math.trunc(bottomSpacerOffset),
        BottomSpacerHeight: Math.trunc(bottomSpacerHeight),
        TopSpacerOffset: Math.trunc(topSpacerOffset),
        TopSpacerHeight: Math.trunc(topSpacerHeight),
        VisibleHeaderHeight: Math.trunc(visibleHeaderHeight),
        VisibleFooterHeight: Math.trunc(visibleFooterHeight),
        TopHiddenRowsHeight: Math.trunc(topHiddenRowsHeight),
        BottomHiddenRowsHeight: Math.trunc(bottomHiddenRowsHeight),
        VisibleRowsHeight: Math.trunc(scrollBoxHeight),
        ScrollTop: Math.trunc(scrollBox.scrollTop),
        SpacerElementsExist: true,
      };
    }

    return {
      ScrollBoxHeight: scrollBox?.clientHeight ?? 0,
      ScrollBoxScrollHeight: scrollBox?.scrollHeight ?? 0,
      ScrollBoxHeaderFooterHeight: 0,
      ListWrapperTopOffset: 0,
      BottomSpacerOffset: 0,
      BottomSpacerHeight: 0,
      TopSpacerOffset: 0,
      TopSpacerHeight: 0,
      VisibleHeaderHeight: 0,
      VisibleFooterHeight: 0,
      TopHiddenRowsHeight: 0,
      BottomHiddenRowsHeight: 0,
      VisibleRowsHeight: 0,
      ScrollTop: 0,
      SpacerElementsExist: false,
    };
  };

  // ---------------------------
  // Scroll Handler Logic
  // ---------------------------
  const handleScroll = async () => {
    const dom = getAllMeasurements();

    if (scrollBox && dom.SpacerElementsExist && componentInstance != null) {
      // Retrieve details about any expanded rows.
      const expandedRowElements = listWrapperRef.querySelectorAll(".expanded-row");
      const expandedRows = Array.from(expandedRowElements).map((e) => ({
        Page: parseInt(e.dataset.page),
        Row: parseInt(e.dataset.row),
        Height: Math.trunc(e.offsetHeight),
      }));

      // If the bottom spacer is visible and has non-zero height, trigger next page.
      if (dom.BottomSpacerOffset < bufferedScrollBoxHeight && dom.BottomSpacerHeight > 0) {
        await componentInstance.invokeMethodAsync(
          HandleNextPage,
          dom.BottomSpacerOffset,
          Math.trunc(bufferedScrollBoxHeight),
          dom.VisibleHeaderHeight,
          dom.VisibleFooterHeight,
          dom.TopHiddenRowsHeight,
          dom.BottomHiddenRowsHeight,
          dom.VisibleRowsHeight,
          expandedRows
        );
      }
      // If the top spacer is visible and has non-zero height, trigger previous page.
      else if (dom.TopSpacerOffset < bufferedScrollBoxHeight && dom.TopSpacerHeight > 0) {
        await componentInstance.invokeMethodAsync(
          HandlePreviousPage,
          dom.TopSpacerOffset,
          Math.trunc(bufferedScrollBoxHeight),
          dom.VisibleHeaderHeight,
          dom.VisibleFooterHeight,
          dom.TopHiddenRowsHeight,
          dom.BottomHiddenRowsHeight,
          dom.VisibleRowsHeight,
          expandedRows
        );
      }
      // Otherwise, report a row visibility change.
      else {
        await componentInstance.invokeMethodAsync(
          HandleRowVisibilityChange,
          dom.TopHiddenRowsHeight,
          dom.BottomHiddenRowsHeight,
          dom.VisibleRowsHeight,
          dom.BottomSpacerOffset,
          dom.ScrollTop,
          expandedRows
        );
      }
    }
  };

  // ---------------------------
  // Resize Handler Logic
  // ---------------------------
  const handleWindowResize = async () => {
    // Recalculate the buffered height after resize.
    bufferedScrollBoxHeight = scrollBox.clientHeight + 2 * bufferPxHeight;
    if (componentInstance != null) {
      await componentInstance.invokeMethodAsync(
        HandleWindowResize,
        window.innerWidth,
        window.innerHeight
      );
    }
  };

  // ---------------------------
  // Attach Event Listeners
  // ---------------------------
  scrollBox.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize);

  // ---------------------------
  // API & Cleanup
  // ---------------------------
  return {
    // Return the actual height of a given row element.
    getRowHeight: (element) => element?.offsetHeight ?? 0,
    // Smoothly scroll the scrollBox to a specific top position.
    scrollTo: (top) => scrollBox?.scroll({ top, behavior: "smooth" }),
    // Scroll the scrollBox by a specified x and y offset.
    scrollBy: (x, y) => scrollBox?.scrollBy(x, y),
    // Get the current window width.
    getWindowWidth: () => window?.innerWidth ?? 0,
    // Get the current window height.
    getWindowHeight: () => window?.innerHeight ?? 0,
    // Expose the measurement function.
    getAllMeasurements,
    // Cleanup event listeners and timers.
    dispose: () => {
      componentInstance = null;
      scrollBox.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (resizeTimer) {
        clearTimeout(resizeTimer);
        resizeTimer = null;
      }
    },
  };
};

// Expose the function globally.
window.initializeTransactionsList = initializeTransactionsList;
if (typeof globalThis === "object") {
  globalThis.initializeTransactionsList = initializeTransactionsList;
}
